/**
 * BulkAddContactsDialog
 * ---------------------
 * Lets an admin add multiple contacts to a hospital under a single department,
 * via either:
 *   1) Paste mode  — paste a list of emails (no names; default name = local-part)
 *   2) Rows mode   — fill in First Name / Last Name / Email per row, so email
 *                    campaigns can use the `{{first_name}}` merge tag properly
 *                    (e.g. "Hi Alice,").
 *
 * Both modes POST to `/api/health-systems/{hs_id}/contacts/bulk-by-department`.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import DepartmentSelect from "@/components/admin/DepartmentSelect";
import { Loader2, UserPlus, Mail, Plus, Trash2, ClipboardPaste, Rows3 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ROW = { first_name: "", last_name: "", email: "" };

function parseClipboardToRows(text) {
  // Parse CSV / TSV / lines pasted from Excel or Google Sheets.
  // Accepts:   "Alice, Smith, alice@hosp.org"
  //            "Alice\tSmith\talice@hosp.org"
  //            "Alice Smith alice@hosp.org"
  //            "alice@hosp.org"
  const out = [];
  const lines = String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    const parts = line.split(/[\t,;]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 3) {
      out.push({ first_name: parts[0], last_name: parts[1], email: parts[2] });
    } else if (parts.length === 2) {
      // "First Last" or "Name email"
      if (parts[1].includes("@")) {
        const [firstName, ...rest] = parts[0].split(/\s+/);
        out.push({ first_name: firstName || "", last_name: rest.join(" "), email: parts[1] });
      } else {
        out.push({ first_name: parts[0], last_name: parts[1], email: "" });
      }
    } else {
      const tokens = parts[0].split(/\s+/);
      if (tokens.length === 1 && tokens[0].includes("@")) {
        out.push({ first_name: "", last_name: "", email: tokens[0] });
      } else if (tokens.length >= 3 && tokens[tokens.length - 1].includes("@")) {
        out.push({ first_name: tokens[0], last_name: tokens.slice(1, -1).join(" "), email: tokens[tokens.length - 1] });
      } else if (tokens.length === 2 && tokens[1].includes("@")) {
        out.push({ first_name: tokens[0], last_name: "", email: tokens[1] });
      }
    }
  }
  return out;
}

export default function BulkAddContactsDialog({
  open,
  onOpenChange,
  hospitalId,
  hospitalName,
  token,
  onSaved,
}) {
  const [department, setDepartment] = useState("Supply Chain");
  const [mode, setMode] = useState("rows"); // "rows" | "paste"
  const [rows, setRows] = useState([{ ...EMPTY_ROW }, { ...EMPTY_ROW }, { ...EMPTY_ROW }]);
  const [emails, setEmails] = useState("");
  const [saving, setSaving] = useState(false);

  const addRow = () => setRows((r) => [...r, { ...EMPTY_ROW }]);
  const removeRow = (i) => setRows((r) => (r.length <= 1 ? [{ ...EMPTY_ROW }] : r.filter((_, idx) => idx !== i)));
  const updateRow = (i, k, v) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const handlePasteIntoRows = (e) => {
    // If user pastes multi-line content into ANY row, expand it into multiple rows.
    const text = e.clipboardData?.getData("text") || "";
    if (!text.includes("\n") && !text.includes("\t")) return; // single value, let default paste happen
    e.preventDefault();
    const parsed = parseClipboardToRows(text);
    if (parsed.length === 0) {
      toast.error("Couldn't parse that paste. Use First, Last, Email columns.");
      return;
    }
    // Merge: keep filled rows on top, append the parsed ones, drop empties
    const nonEmpty = rows.filter((r) => r.first_name || r.last_name || r.email);
    setRows([...nonEmpty, ...parsed, ...(nonEmpty.length + parsed.length === 0 ? [{ ...EMPTY_ROW }] : [])]);
    toast.success(`Pasted ${parsed.length} row${parsed.length === 1 ? "" : "s"}`);
  };

  const validRowCount = useMemo(
    () => rows.filter((r) => (r.email || "").trim().includes("@")).length,
    [rows]
  );

  const submit = async () => {
    let body;
    if (mode === "rows") {
      const entries = rows
        .map((r) => ({
          first_name: (r.first_name || "").trim(),
          last_name: (r.last_name || "").trim(),
          email: (r.email || "").trim(),
        }))
        .filter((r) => r.email.includes("@"));
      if (entries.length === 0) {
        toast.error("Add at least one row with a valid email");
        return;
      }
      body = { department, entries, emails: [] };
    } else {
      const list = (emails || "")
        .replace(/[;\n]/g, ",")
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      if (list.length === 0) {
        toast.error("Paste at least one email address");
        return;
      }
      body = { department, emails: list };
    }

    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/health-systems/${hospitalId}/contacts/bulk-by-department`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const added = r.data?.added_count ?? (r.data?.added?.length || 0);
      const skipped_dupes = r.data?.skipped_duplicates?.length || 0;
      const skipped_users = r.data?.skipped_portal_users?.length || 0;
      const parts = [
        `Added ${added} ${department} contact${added === 1 ? "" : "s"}`,
      ];
      if (skipped_dupes) parts.push(`skipped ${skipped_dupes} duplicate${skipped_dupes === 1 ? "" : "s"}`);
      if (skipped_users) parts.push(`skipped ${skipped_users} already-registered portal user${skipped_users === 1 ? "" : "s"}`);
      toast.success(parts.join(" · "), { duration: 6000 });
      setEmails("");
      setRows([{ ...EMPTY_ROW }, { ...EMPTY_ROW }, { ...EMPTY_ROW }]);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = "Bulk add failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d.msg || "").join("; ");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="bulk-add-contacts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" /> Bulk add contacts to {hospitalName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category / department *</Label>
            <DepartmentSelect
              value={department}
              onChange={setDepartment}
              testId="bulk-add-department"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              All contacts will be saved under this department. You can then target them as a bulk group from Email Campaigns.
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2 border-b border-slate-200">
            <button
              type="button"
              onClick={() => setMode("rows")}
              className={`px-4 py-2 -mb-px text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 ${mode === "rows" ? "text-blue-700 border-blue-600" : "text-slate-500 border-transparent hover:text-slate-800"}`}
              data-testid="bulk-add-mode-rows"
            >
              <Rows3 className="w-4 h-4" />
              First &amp; Last Name rows
            </button>
            <button
              type="button"
              onClick={() => setMode("paste")}
              className={`px-4 py-2 -mb-px text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 ${mode === "paste" ? "text-blue-700 border-blue-600" : "text-slate-500 border-transparent hover:text-slate-800"}`}
              data-testid="bulk-add-mode-paste"
            >
              <ClipboardPaste className="w-4 h-4" />
              Paste emails only
            </button>
          </div>

          {mode === "rows" && (
            <div>
              <div className="grid grid-cols-[1fr_1fr_1.4fr_30px] gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 px-1 mb-1">
                <span>First name</span>
                <span>Last name</span>
                <span>Email *</span>
                <span></span>
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1" data-testid="bulk-add-rows-container">
                {rows.map((row, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_30px] gap-2" data-testid={`bulk-add-row-${i}`}>
                    <Input
                      value={row.first_name}
                      onChange={(e) => updateRow(i, "first_name", e.target.value)}
                      onPaste={handlePasteIntoRows}
                      placeholder="Alice"
                      data-testid={`bulk-add-first-${i}`}
                    />
                    <Input
                      value={row.last_name}
                      onChange={(e) => updateRow(i, "last_name", e.target.value)}
                      onPaste={handlePasteIntoRows}
                      placeholder="Smith"
                      data-testid={`bulk-add-last-${i}`}
                    />
                    <Input
                      type="email"
                      value={row.email}
                      onChange={(e) => updateRow(i, "email", e.target.value)}
                      onPaste={handlePasteIntoRows}
                      placeholder="alice@hospital.org"
                      data-testid={`bulk-add-email-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="text-slate-300 hover:text-red-500 flex items-center justify-center"
                      title="Remove row"
                      data-testid={`bulk-add-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRow}
                  className="gap-1.5"
                  data-testid="bulk-add-row-add"
                >
                  <Plus className="w-4 h-4" /> Add row
                </Button>
                <span className="text-[11px] text-slate-500">
                  {validRowCount} valid row{validRowCount === 1 ? "" : "s"} · paste from Excel into any cell auto-expands
                </span>
              </div>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800 flex items-start gap-2 mt-3">
                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Email campaigns will use the <strong>First name</strong> field for personalization (e.g. &ldquo;Hi Alice,&rdquo;) via the <code className="bg-white px-1 rounded text-[11px]">{`{{first_name}}`}</code> merge tag.
                </span>
              </div>
            </div>
          )}

          {mode === "paste" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email addresses *</Label>
              <Textarea
                rows={6}
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder={"alice@hosp.org, bob@hosp.org\ncarol@hosp.org\ndave@hosp.org;eve@hosp.org"}
                data-testid="bulk-add-emails"
                className="font-mono text-xs"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Paste any combination — comma, semicolon, or newline-separated. Names will default to the email&rsquo;s local-part. Use the <strong>First &amp; Last Name rows</strong> tab to get proper &ldquo;Hi Alice,&rdquo; personalization.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
            data-testid="bulk-add-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Save contacts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
